import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('hpme_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchUser = async () => {
    try {
      // Hard 10s timeout — if /auth/me hangs (stale CDN cache, network blip,
      // server hot-reload), we MUST resolve loading=false so the user isn't
      // trapped on an infinite ProtectedRoute spinner.
      const response = await axios.get(`${API}/auth/me`, { timeout: 10000 });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      // Treat 401 OR network/timeout error as "session expired" → clear token,
      // let the ProtectedRoute redirect to public landing.
      if (error.response?.status === 401 || error.code === 'ECONNABORTED' || !error.response) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('hpme_token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, role = 'athlete') => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, role });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('hpme_token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  // Used by chat-native login / signup / password-reset cards.
  // Accepts a token+user already produced by /api/auth/*-chat endpoints
  // and installs them without a round-trip.
  const loginFromResponse = (newToken, userData) => {
    localStorage.setItem('hpme_token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('hpme_token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const updateProfile = async (profileData) => {
    const response = await axios.put(`${API}/auth/profile`, profileData);
    setUser(response.data);
    return response.data;
  };

  const uploadAvatar = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const response = await axios.post(`${API}/auth/avatar`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    setUser(response.data);
    return response.data;
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, updateProfile, uploadAvatar, loginFromResponse }}>
      {children}
    </AuthContext.Provider>
  );
};
